import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  SimpleButton,
  LoadingButton,
  TextArea,
  Select,
  Input,
  NotificationsList,
  Spinner,
  useStateContext,
} from "@veda/dashboard-ui";
import {
  BsCheckCircle,
  BsClockHistory,
  BsXCircle,
  BsListCheck,
  BsPeople,
  BsFileText,
  BsCheckSquare,
  BsFileEarmarkText,
  BsPaperclip,
} from "react-icons/bs";
import { usePppApi } from "../services/pppApi";
import PppDialog from "./PppDialog";
import { useAuthUser } from "../hooks/useAuthUser";
import { useDownload } from "../hooks/useDownload";

// ─── Costanti ────────────────────────────────────────────────────────────────

const STATO_INTEGRAZIONE_CFG = {
  in_corso:   { cls: "bg-yellow-100 text-yellow-800",  label: "In corso" },
  completato: { cls: "bg-blue-100 text-blue-800",      label: "Completato" },
  approvato:  { cls: "bg-green-100 text-green-800",    label: "Approvato" },
};

const TIPO_PARERE_OPTIONS = [
  { value: "parere",         label: "Parere" },
  { value: "autorizzazione", label: "Autorizzazione" },
  { value: "intesa",         label: "Intesa" },
  { value: "nulla_osta",     label: "Nulla Osta" },
];

const STATO_PARERE_CFG = {
  richiesto:       { cls: "bg-gray-100 text-gray-700",   label: "Richiesto" },
  acquisito:       { cls: "bg-green-100 text-green-800", label: "Acquisito" },
  negato:          { cls: "bg-red-100 text-red-800",     label: "Negato" },
  silenzio_assenso:{ cls: "bg-blue-100 text-blue-700",   label: "Silenzio assenso" },
};

const MODALITA_CDS_OPTIONS = [
  { value: "asincrona", label: "Semplificata asincrona (art. 14-bis — 30 gg)" },
  { value: "sincrona",  label: "Simultanea sincrona (art. 14-ter — 30 gg)" },
];

const STATO_CDS_CFG = {
  programmata: { cls: "bg-yellow-100 text-yellow-800", label: "Programmata" },
  in_corso:    { cls: "bg-blue-100 text-blue-700",     label: "In corso" },
  conclusa:    { cls: "bg-green-100 text-green-800",   label: "Conclusa" },
  annullata:   { cls: "bg-red-100 text-red-800",       label: "Annullata" },
};

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("it-IT");
}

function StatoBadge({ stato, cfg }) {
  const c = cfg[stato];
  if (!c) return null;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${c.cls}`}>
      {c.label}
    </span>
  );
}

// ─── Checklist elaborati ──────────────────────────────────────────────────────

function ChecklistPanel({ integrazione, onAggiorna, canGestisci, procedimentoId, propostaId, loading }) {
  const checklist = integrazione?.checklist_elaborati ?? [];
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(checklist);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState(null);
  const api = usePppApi();
  const { addToast } = useStateContext();

  useEffect(() => {
    setDraft(integrazione?.checklist_elaborati ?? []);
  }, [integrazione]);

  const togglePresente = (idx) => {
    setDraft((prev) => prev.map((el, i) => i === idx ? { ...el, presente: !el.presente } : el));
  };

  const handleSalva = () => {
    setSaving(true);
    setErrors(null);
    api.aggiornaChecklistPfte(procedimentoId, propostaId, integrazione.id, { checklist_elaborati: draft }, {
      onSuccess: () => {
        setSaving(false);
        setEditing(false);
        addToast({ type: "success", message: "Checklist aggiornata." });
        onAggiorna();
      },
      onFormError: (body, text) => {
        setSaving(false);
        setErrors([text]);
      },
    });
  };

  const totale = checklist.length;
  const presenti = checklist.filter((el) => el.presente).length;

  return (
    <div className="border border-gray-200 rounded-lg bg-white p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BsListCheck className="text-blue-500 text-lg" />
          <h4 className="text-sm font-semibold text-gray-800">Checklist elaborati integrativi (Allegato I.7)</h4>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">
            {presenti}/{totale} forniti
          </span>
          {canGestisci && !integrazione?.approvato && (
            <SimpleButton
              text={editing ? "Annulla" : "Modifica"}
              customFunc={() => { setEditing((e) => !e); setDraft(checklist); setErrors(null); }}
            />
          )}
        </div>
      </div>

      {errors && <NotificationsList errors={errors} />}

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="text-left py-2 px-2 text-xs font-medium text-gray-500 w-16">Codice</th>
              <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">Elaborato</th>
              <th className="text-center py-2 px-2 text-xs font-medium text-gray-500 w-24">Presente</th>
            </tr>
          </thead>
          <tbody>
            {(editing ? draft : checklist).map((el, idx) => (
              <tr key={el.codice} className="border-b border-gray-50 hover:bg-gray-50">
                <td className="py-2 px-2 text-xs text-gray-500 font-mono">{el.codice}</td>
                <td className="py-2 px-2 text-gray-700">{el.nome}</td>
                <td className="py-2 px-2 text-center">
                  {editing ? (
                    <input
                      type="checkbox"
                      checked={el.presente}
                      onChange={() => togglePresente(idx)}
                      className="h-4 w-4 text-blue-600 rounded cursor-pointer"
                    />
                  ) : el.presente ? (
                    <BsCheckCircle className="mx-auto text-green-500 text-base" />
                  ) : (
                    <BsXCircle className="mx-auto text-gray-300 text-base" />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="flex justify-end gap-2">
          <SimpleButton text="Annulla" customFunc={() => { setEditing(false); setDraft(checklist); }} />
          <LoadingButton isLoading={saving} text="Salva checklist" customFunc={handleSalva} />
        </div>
      )}
    </div>
  );
}

// ─── Pareri / Autorizzazioni ──────────────────────────────────────────────────

function PareriPanel({ integrazione, procedimentoId, propostaId, canGestisci, onAggiorna }) {
  const [pareri, setPareri] = useState([]);
  const [loadingPareri, setLoadingPareri] = useState(true);
  const [openNuovo, setOpenNuovo] = useState(false);
  const [openAggStato, setOpenAggStato] = useState(null);
  const [form, setForm] = useState({ tipo: "", amministrazione_emittente: "", oggetto: "", data_richiesta: "", termine_risposta: "" });
  const [statoForm, setStatoForm] = useState({ stato: "", data_acquisizione: "" });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState(null);
  const api = usePppApi();
  const { addToast } = useStateContext();

  const loadPareri = useCallback(() => {
    if (!integrazione) return;
    setLoadingPareri(true);
    api.listPareriFase13(procedimentoId, propostaId, integrazione.id, {
      onSuccess: (data) => { setPareri(data); setLoadingPareri(false); },
      onFormError: () => setLoadingPareri(false),
    });
  }, [procedimentoId, propostaId, integrazione]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadPareri(); }, [loadPareri]);

  const handleRegistra = () => {
    setErrors(null);
    if (!form.tipo || !form.amministrazione_emittente || !form.oggetto) {
      setErrors(["Tipo, amministrazione emittente e oggetto sono obbligatori."]);
      return;
    }
    setSaving(true);
    api.registraParereFase13(procedimentoId, propostaId, integrazione.id, form, {
      onSuccess: () => {
        setSaving(false);
        setOpenNuovo(false);
        setForm({ tipo: "", amministrazione_emittente: "", oggetto: "", data_richiesta: "", termine_risposta: "" });
        addToast({ type: "success", message: "Parere/autorizzazione registrato." });
        loadPareri();
        onAggiorna();
      },
      onFormError: (body, text) => { setSaving(false); setErrors([text]); },
    });
  };

  const handleAggStato = () => {
    setErrors(null);
    if (!statoForm.stato) { setErrors(["Seleziona il nuovo stato."]); return; }
    setSaving(true);
    api.aggiornaStatoParereFase13(procedimentoId, propostaId, integrazione.id, openAggStato.id, statoForm, {
      onSuccess: () => {
        setSaving(false);
        setOpenAggStato(null);
        addToast({ type: "success", message: "Stato aggiornato." });
        loadPareri();
      },
      onFormError: (body, text) => { setSaving(false); setErrors([text]); },
    });
  };

  return (
    <div className="border border-gray-200 rounded-lg bg-white p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BsFileText className="text-purple-500 text-lg" />
          <h4 className="text-sm font-semibold text-gray-800">Pareri, autorizzazioni e intese (art. 38)</h4>
        </div>
        {canGestisci && integrazione && (
          <SimpleButton text="+ Registra" customFunc={() => { setOpenNuovo(true); setErrors(null); }} />
        )}
      </div>

      {loadingPareri ? (
        <div className="flex justify-center py-4"><Spinner /></div>
      ) : pareri.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-4">Nessun parere registrato.</p>
      ) : (
        <div className="space-y-2">
          {pareri.map((p) => (
            <div key={p.id} className="border border-gray-100 rounded-lg p-3 flex items-start justify-between gap-3 text-sm">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <StatoBadge stato={p.stato} cfg={STATO_PARERE_CFG} />
                  <span className="text-gray-500 text-xs capitalize">{p.tipo.replace("_", " ")}</span>
                </div>
                <p className="font-medium text-gray-800">{p.amministrazione_emittente}</p>
                <p className="text-gray-500 text-xs mt-0.5">{p.oggetto}</p>
                {p.termine_risposta && (
                  <p className="text-xs text-gray-400 mt-1">Scadenza: {formatDate(p.termine_risposta)}</p>
                )}
              </div>
              {canGestisci && p.stato === "richiesto" && (
                <SimpleButton text="Aggiorna" customFunc={() => { setOpenAggStato(p); setStatoForm({ stato: "", data_acquisizione: "" }); setErrors(null); }} />
              )}
            </div>
          ))}
        </div>
      )}

      <PppDialog
        open={openNuovo}
        title="Registra parere / autorizzazione / intesa"
        onClose={() => setOpenNuovo(false)}
        footer={
          <>
            <SimpleButton text="Annulla" customFunc={() => setOpenNuovo(false)} />
            <LoadingButton isLoading={saving} text="Registra" customFunc={handleRegistra} />
          </>
        }
      >
        <div className="space-y-3">
          <NotificationsList errors={errors} />
          <Select
            label="Tipo *"
            value={form.tipo}
            onChange={(e) => setForm((f) => ({ ...f, tipo: e.target.value }))}
            options={[{ value: "", label: "— Seleziona tipo —" }, ...TIPO_PARERE_OPTIONS]}
          />
          <Input
            label="Amministrazione emittente *"
            value={form.amministrazione_emittente}
            onChange={(e) => setForm((f) => ({ ...f, amministrazione_emittente: e.target.value }))}
            placeholder="Es. Soprintendenza ABAP, ARPA, Comune..."
          />
          <Input
            label="Oggetto *"
            value={form.oggetto}
            onChange={(e) => setForm((f) => ({ ...f, oggetto: e.target.value }))}
            placeholder="Oggetto del parere / autorizzazione..."
          />
          <Input
            type="date"
            label="Data richiesta"
            value={form.data_richiesta}
            onChange={(e) => setForm((f) => ({ ...f, data_richiesta: e.target.value }))}
          />
          <Input
            type="date"
            label="Termine risposta"
            value={form.termine_risposta}
            onChange={(e) => setForm((f) => ({ ...f, termine_risposta: e.target.value }))}
          />
        </div>
      </PppDialog>

      <PppDialog
        open={!!openAggStato}
        title="Aggiorna stato parere"
        onClose={() => setOpenAggStato(null)}
        footer={
          <>
            <SimpleButton text="Annulla" customFunc={() => setOpenAggStato(null)} />
            <LoadingButton isLoading={saving} text="Aggiorna" customFunc={handleAggStato} />
          </>
        }
      >
        <div className="space-y-3">
          <NotificationsList errors={errors} />
          <Select
            label="Nuovo stato *"
            value={statoForm.stato}
            onChange={(e) => setStatoForm((f) => ({ ...f, stato: e.target.value }))}
            options={[
              { value: "", label: "— Seleziona stato —" },
              { value: "acquisito",        label: "Acquisito" },
              { value: "negato",           label: "Negato" },
              { value: "silenzio_assenso", label: "Silenzio assenso" },
            ]}
          />
          <Input
            type="date"
            label="Data acquisizione / risposta"
            value={statoForm.data_acquisizione}
            onChange={(e) => setStatoForm((f) => ({ ...f, data_acquisizione: e.target.value }))}
          />
        </div>
      </PppDialog>
    </div>
  );
}

// ─── Conferenza di Servizi Decisoria ──────────────────────────────────────────

function ConferenzaDecistoriaPanel({ integrazione, procedimentoId, propostaId, canCrea, canGestisci, onAggiorna }) {
  const [conferenze, setConferenze] = useState([]);
  const [loadingCds, setLoadingCds] = useState(true);
  const [openNuova, setOpenNuova] = useState(false);
  const [openChiudi, setOpenChiudi] = useState(null);
  const [form, setForm] = useState({
    modalita: "",
    motivazione_attivazione: "",
    amministrazioni_coinvolte: "",
    termine_determinazioni_giorni: "30",
    data_convocazione: "",
  });
  const [chiudiForm, setChiudiForm] = useState({
    verbale: "",
    determinazione_finale: "",
    determinazione_motivata: "",
    data_determinazione_motivata: "",
    effetto_sostitutivo: false,
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState(null);
  const api = usePppApi();
  const { addToast } = useStateContext();

  const loadConferenze = useCallback(() => {
    if (!integrazione) return;
    setLoadingCds(true);
    api.listConferenzeF13(procedimentoId, propostaId, integrazione.id, {
      onSuccess: (data) => { setConferenze(data); setLoadingCds(false); },
      onFormError: () => setLoadingCds(false),
    });
  }, [procedimentoId, propostaId, integrazione]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadConferenze(); }, [loadConferenze]);

  const handleAttiva = () => {
    setErrors(null);
    const amministrazioni = form.amministrazioni_coinvolte
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    if (!form.modalita) { setErrors(["Seleziona la modalità della conferenza."]); return; }
    if (form.motivazione_attivazione.length < 20) { setErrors(["La motivazione deve avere almeno 20 caratteri."]); return; }
    if (amministrazioni.length === 0) { setErrors(["Indicare almeno un'amministrazione coinvolta."]); return; }
    setSaving(true);
    api.attivaConferenzaF13(procedimentoId, propostaId, integrazione.id, {
      ...form,
      amministrazioni_coinvolte: amministrazioni,
      termine_determinazioni_giorni: parseInt(form.termine_determinazioni_giorni, 10),
    }, {
      onSuccess: () => {
        setSaving(false);
        setOpenNuova(false);
        addToast({ type: "success", message: "Conferenza di Servizi Decisoria attivata." });
        loadConferenze();
        onAggiorna();
      },
      onFormError: (body, text) => { setSaving(false); setErrors([text]); },
    });
  };

  const handleChiudi = () => {
    setErrors(null);
    if (chiudiForm.verbale.length < 20) { setErrors(["Il verbale deve avere almeno 20 caratteri."]); return; }
    if (chiudiForm.determinazione_motivata.length < 20) { setErrors(["La determinazione motivata deve avere almeno 20 caratteri."]); return; }
    setSaving(true);
    api.chiudiConferenzaF13(procedimentoId, propostaId, integrazione.id, openChiudi.id, chiudiForm, {
      onSuccess: () => {
        setSaving(false);
        setOpenChiudi(null);
        addToast({ type: "success", message: "Conferenza di Servizi conclusa." });
        loadConferenze();
        onAggiorna();
      },
      onFormError: (body, text) => { setSaving(false); setErrors([text]); },
    });
  };

  return (
    <div className="border border-gray-200 rounded-lg bg-white p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BsPeople className="text-indigo-500 text-lg" />
          <h4 className="text-sm font-semibold text-gray-800">
            Conferenza di Servizi Decisoria (art. 38 D.lgs 36/2023)
          </h4>
        </div>
        {canCrea && integrazione && !integrazione.approvato && (
          <SimpleButton text="+ Attiva CdS" customFunc={() => { setOpenNuova(true); setErrors(null); }} />
        )}
      </div>

      {loadingCds ? (
        <div className="flex justify-center py-4"><Spinner /></div>
      ) : conferenze.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-4">Nessuna conferenza di servizi attivata.</p>
      ) : (
        <div className="space-y-3">
          {conferenze.map((cds) => (
            <div key={cds.id} className="border border-gray-100 rounded-lg p-4 space-y-2 text-sm">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <StatoBadge stato={cds.stato} cfg={STATO_CDS_CFG} />
                  <span className="text-xs text-gray-500 capitalize">
                    {cds.modalita === "asincrona" ? "Semplificata asincrona (art. 14-bis)" : "Simultanea sincrona (art. 14-ter)"}
                  </span>
                  {cds.effetto_sostitutivo && (
                    <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-semibold rounded">
                      Effetto sostitutivo
                    </span>
                  )}
                </div>
                {canGestisci && cds.stato !== "conclusa" && cds.stato !== "annullata" && (
                  <SimpleButton
                    text="Chiudi CdS"
                    customFunc={() => {
                      setOpenChiudi(cds);
                      setChiudiForm({ verbale: "", determinazione_finale: "", determinazione_motivata: "", data_determinazione_motivata: "", effetto_sostitutivo: false });
                      setErrors(null);
                    }}
                  />
                )}
              </div>
              <div className="text-xs text-gray-500 space-y-0.5">
                {cds.data_convocazione && <p>Convocazione: {formatDate(cds.data_convocazione)}</p>}
                {cds.termine_determinazioni_giorni && (
                  <p>Termine determinazioni: {cds.termine_determinazioni_giorni} giorni</p>
                )}
                {cds.amministrazioni_coinvolte?.length > 0 && (
                  <p>Amministrazioni: {cds.amministrazioni_coinvolte.join(", ")}</p>
                )}
              </div>
              {cds.determinazione_motivata && (
                <div className="bg-gray-50 rounded p-2 mt-2">
                  <p className="text-xs font-medium text-gray-600 mb-1">Determinazione motivata conclusiva (art. 14-quater)</p>
                  <p className="text-xs text-gray-700 whitespace-pre-wrap">{cds.determinazione_motivata.slice(0, 200)}{cds.determinazione_motivata.length > 200 ? "..." : ""}</p>
                  {cds.data_determinazione_motivata && (
                    <p className="text-xs text-gray-400 mt-1">Data: {formatDate(cds.data_determinazione_motivata)}</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Dialog — attiva CdS */}
      <PppDialog
        open={openNuova}
        title="Attiva Conferenza di Servizi Decisoria"
        onClose={() => setOpenNuova(false)}
        footer={
          <>
            <SimpleButton text="Annulla" customFunc={() => setOpenNuova(false)} />
            <LoadingButton isLoading={saving} text="Attiva conferenza" customFunc={handleAttiva} />
          </>
        }
      >
        <div className="space-y-3">
          <NotificationsList errors={errors} />
          <Select
            label="Modalità *"
            value={form.modalita}
            onChange={(e) => setForm((f) => ({ ...f, modalita: e.target.value }))}
            options={[{ value: "", label: "— Seleziona modalità —" }, ...MODALITA_CDS_OPTIONS]}
          />
          <TextArea
            label="Motivazione attivazione *"
            value={form.motivazione_attivazione}
            onChange={(e) => setForm((f) => ({ ...f, motivazione_attivazione: e.target.value }))}
            placeholder="Necessità di acquisire pareri obbligatori per l'approvazione del PFTE... (min. 20 caratteri)"
            rows={4}
          />
          <TextArea
            label="Amministrazioni coinvolte * (una per riga)"
            value={form.amministrazioni_coinvolte}
            onChange={(e) => setForm((f) => ({ ...f, amministrazioni_coinvolte: e.target.value }))}
            placeholder={"Soprintendenza ABAP\nARPA\nComune di..."}
            rows={3}
          />
          <Input
            label="Termine determinazioni (giorni) *"
            type="number"
            min="1"
            max="90"
            value={form.termine_determinazioni_giorni}
            onChange={(e) => setForm((f) => ({ ...f, termine_determinazioni_giorni: e.target.value }))}
          />
          <Input
            type="date"
            label="Data convocazione (lascia vuoto per calcolo automatico)"
            value={form.data_convocazione}
            onChange={(e) => setForm((f) => ({ ...f, data_convocazione: e.target.value }))}
          />
        </div>
      </PppDialog>

      {/* Dialog — chiudi CdS */}
      <PppDialog
        open={!!openChiudi}
        title="Chiudi Conferenza di Servizi — Determinazione conclusiva"
        onClose={() => setOpenChiudi(null)}
        footer={
          <>
            <SimpleButton text="Annulla" customFunc={() => setOpenChiudi(null)} />
            <LoadingButton isLoading={saving} text="Chiudi conferenza" customFunc={handleChiudi} />
          </>
        }
      >
        <div className="space-y-3">
          <NotificationsList errors={errors} />
          <TextArea
            label="Verbale della conferenza *"
            value={chiudiForm.verbale}
            onChange={(e) => setChiudiForm((f) => ({ ...f, verbale: e.target.value }))}
            placeholder="Descrizione svolgimento della conferenza di servizi..."
            rows={4}
          />
          <TextArea
            label="Determinazione finale *"
            value={chiudiForm.determinazione_finale}
            onChange={(e) => setChiudiForm((f) => ({ ...f, determinazione_finale: e.target.value }))}
            placeholder="Sintesi della determinazione adottata..."
            rows={3}
          />
          <TextArea
            label="Determinazione motivata conclusiva (art. 14-quater) *"
            value={chiudiForm.determinazione_motivata}
            onChange={(e) => setChiudiForm((f) => ({ ...f, determinazione_motivata: e.target.value }))}
            placeholder="Testo completo della determinazione motivata con effetto sostitutivo ai sensi dell'art. 14-quater L. 241/1990..."
            rows={5}
          />
          <Input
            type="date"
            label="Data determinazione motivata"
            value={chiudiForm.data_determinazione_motivata}
            onChange={(e) => setChiudiForm((f) => ({ ...f, data_determinazione_motivata: e.target.value }))}
          />
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="effetto_sostitutivo"
              checked={chiudiForm.effetto_sostitutivo}
              onChange={(e) => setChiudiForm((f) => ({ ...f, effetto_sostitutivo: e.target.checked }))}
              className="h-4 w-4 text-blue-600 rounded cursor-pointer"
            />
            <label htmlFor="effetto_sostitutivo" className="text-sm text-gray-700 cursor-pointer">
              Esercizio dell'effetto sostitutivo (art. 14-quater, co. 1)
            </label>
          </div>
        </div>
      </PppDialog>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function Fase13Tab({ procedimentoId, propostaId, proposta, onPropostaReload }) {
  const [integrazione, setIntegrazione] = useState(null);
  const [loading, setLoading] = useState(true);
  const [avvioSaving, setAvvioSaving] = useState(false);
  const [avvioErrors, setAvvioErrors] = useState(null);
  const [openVerifica, setOpenVerifica] = useState(false);
  const [openApprova, setOpenApprova] = useState(false);
  const [verificaForm, setVerificaForm] = useState({ inserito_programmazione: false, data_inserimento_programmazione: "", note_programmazione: "" });
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState(null);
  const [uploadingVerifica, setUploadingVerifica] = useState(false);
  const [tipoFileVerifica, setTipoFileVerifica] = useState("verbale");
  const fileVerificaRef = useRef(null);
  const api = usePppApi();
  const { addToast } = useStateContext();
  const { can } = useAuthUser();

  const { download } = useDownload();
  const canAvvia       = can("ppp.integrazione_pfte.create");
  const canGestisci    = can("ppp.integrazione_pfte.manage");
  const canApprova     = can("ppp.integrazione_pfte.approve");
  const canViewPareri  = can("ppp.pareri_autorizzazioni.view");
  const canGestisciPareri = can("ppp.pareri_autorizzazioni.manage");
  const canCreaCds     = can("ppp.conferenza_servizi_f13.create");
  const canGestisciCds = can("ppp.conferenza_servizi_f13.manage");

  const integrazioneId = proposta?.integrazionePfteId;

  const load = useCallback(() => {
    if (!integrazioneId) { setLoading(false); return; }
    setLoading(true);
    api.getIntegrazionePfte(procedimentoId, propostaId, integrazioneId, {
      onSuccess: (data) => { setIntegrazione(data); setLoading(false); },
      onFormError: () => setLoading(false),
    });
  }, [integrazioneId, procedimentoId, propostaId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const handleAvvia = () => {
    setAvvioErrors(null);
    setAvvioSaving(true);
    api.avviaIntegrazionePfte(procedimentoId, propostaId, {}, {
      onSuccess: () => {
        setAvvioSaving(false);
        addToast({ type: "success", message: "Fase 13 avviata. Checklist elaborati inizializzata." });
        if (onPropostaReload) onPropostaReload(); else window.location.reload();
      },
      onFormError: (body, text) => { setAvvioSaving(false); setAvvioErrors([text]); },
    });
  };

  const handleVerificaCompletezza = () => {
    setFormErrors(null);
    const file = fileVerificaRef.current?.files?.[0];

    const inviaVerifica = (documentoVerificaId) => {
      setSaving(true);
      api.verificaCompletezzaPfte(procedimentoId, propostaId, integrazione.id, {
        ...verificaForm,
        documento_verifica_id: documentoVerificaId ?? null,
      }, {
        onSuccess: () => {
          setSaving(false);
          setOpenVerifica(false);
          if (fileVerificaRef.current) fileVerificaRef.current.value = "";
          setTipoFileVerifica("verbale");
          addToast({ type: "success", message: "Verifica di completezza registrata." });
          load();
        },
        onFormError: (body, text) => { setSaving(false); setFormErrors([text]); },
      });
    };

    if (file) {
      setUploadingVerifica(true);
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tipo", tipoFileVerifica);
      fd.append("propostaId", propostaId);
      api.uploadDocumento(procedimentoId, fd, {
        onSuccess: (res) => {
          setUploadingVerifica(false);
          const docId = res?.id;
          if (!docId) { setFormErrors(["Errore durante il caricamento del file."]); return; }
          inviaVerifica(docId);
        },
        onError: () => { setUploadingVerifica(false); setFormErrors(["Errore durante il caricamento del file."]); },
        onFormError: (body, text) => { setUploadingVerifica(false); setFormErrors([text]); },
      });
    } else {
      inviaVerifica(null);
    }
  };

  const handleApprova = () => {
    setFormErrors(null);
    setSaving(true);
    api.approvaIntegrazionePfte(procedimentoId, propostaId, integrazione.id, {}, {
      onSuccess: () => {
        setSaving(false);
        setOpenApprova(false);
        addToast({ type: "success", message: "PFTE approvato. Fase 13 completata." });
        load();
      },
      onFormError: (body, text) => { setSaving(false); setFormErrors([text]); },
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-base font-semibold text-gray-800">
            Fase 13 — Integrazione e Approvazione PFTE
          </h3>
          <p className="text-xs text-gray-400 mt-0.5">
            Art. 193, commi 7 e 8 D.lgs 36/2023 — Art. 38 D.lgs 36/2023
          </p>
        </div>
        {integrazione && (
          <StatoBadge stato={integrazione.stato} cfg={STATO_INTEGRAZIONE_CFG} />
        )}
      </div>

      {/* Avvio fase */}
      {!integrazione && proposta?.stato === "ammessa" && (
        <div className="border border-dashed border-blue-300 rounded-lg bg-blue-50 p-6 text-center space-y-3">
          <BsCheckCircle className="mx-auto text-blue-400 text-3xl" />
          <p className="text-sm text-blue-800 font-medium">
            La proposta è stata ammessa. Avvia la Fase 13 per integrare il PFTE e procedere all'approvazione.
          </p>
          <p className="text-xs text-blue-600">
            Artt. 193 co. 7-8 + art. 38 D.lgs 36/2023 — la checklist degli elaborati integrativi (Allegato I.7) verrà inizializzata automaticamente.
          </p>
          {avvioErrors && <NotificationsList errors={avvioErrors} />}
          {canAvvia && (
            <div className="flex justify-center">
              <LoadingButton isLoading={avvioSaving} text="Avvia Fase 13 — Integrazione PFTE" customFunc={handleAvvia} />
            </div>
          )}
        </div>
      )}

      {!integrazione && proposta?.stato !== "ammessa" && (
        <div className="text-center py-10 text-gray-400 text-sm">
          <BsClockHistory className="mx-auto mb-2 text-2xl" />
          La Fase 13 si attiva solo dopo l'ammissione della proposta (Fase 12).
        </div>
      )}

      {integrazione && (
        <>
          {/* Barra azioni principali */}
          <div className="flex items-center gap-2 flex-wrap">
            {canGestisci && !integrazione.verifica_completezza && integrazione.stato !== "in_corso" && (
              <SimpleButton
                text="Verifica completezza"
                customFunc={() => {
                  setOpenVerifica(true);
                  setFormErrors(null);
                  setTipoFileVerifica("verbale");
                  if (fileVerificaRef.current) fileVerificaRef.current.value = "";
                }}
              />
            )}
            {integrazione.verifica_completezza && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 text-xs rounded border border-green-200">
                  <BsCheckSquare /> Completezza verificata il {formatDate(integrazione.data_verifica_completezza)}
                </span>
                {integrazione.documento_verifica_id && (
                  <button
                    onClick={() => download(
                      `/api/v1/ppp/procedimenti/${procedimentoId}/documenti/${integrazione.documento_verifica_id}/download`,
                      integrazione.documento_verifica_nome ?? "verbale_verifica"
                    )}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700 hover:bg-blue-100 transition-colors"
                  >
                    <BsFileEarmarkText className="shrink-0" />
                    {integrazione.documento_verifica_nome ?? "Verbale verifica"}
                  </button>
                )}
              </div>
            )}
            {canApprova && integrazione.verifica_completezza && !integrazione.approvato && (
              <SimpleButton
                text="Approva PFTE"
                customFunc={() => { setOpenApprova(true); setFormErrors(null); }}
              />
            )}
            {integrazione.approvato && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 text-xs rounded border border-green-200">
                <BsCheckCircle /> PFTE approvato il {formatDate(integrazione.approvato_il)} da {integrazione.approvato_da}
              </span>
            )}
          </div>

          {/* Checklist elaborati */}
          <ChecklistPanel
            integrazione={integrazione}
            procedimentoId={procedimentoId}
            propostaId={propostaId}
            canGestisci={canGestisci}
            onAggiorna={load}
          />

          {/* Pareri e autorizzazioni */}
          {(canViewPareri || canGestisciPareri) && (
            <PareriPanel
              integrazione={integrazione}
              procedimentoId={procedimentoId}
              propostaId={propostaId}
              canGestisci={canGestisciPareri}
              onAggiorna={load}
            />
          )}

          {/* Conferenza di Servizi Decisoria */}
          {(canCreaCds || canGestisciCds) && (
            <ConferenzaDecistoriaPanel
              integrazione={integrazione}
              procedimentoId={procedimentoId}
              propostaId={propostaId}
              canCrea={canCreaCds}
              canGestisci={canGestisciCds}
              onAggiorna={load}
            />
          )}
        </>
      )}

      {/* Dialog — verifica completezza */}
      <PppDialog
        open={openVerifica}
        title="Verifica completezza elaborati PFTE"
        onClose={() => setOpenVerifica(false)}
        footer={
          <>
            <SimpleButton text="Annulla" customFunc={() => setOpenVerifica(false)} />
            <span className={uploadingVerifica || saving ? "opacity-50 pointer-events-none" : ""}>
              <LoadingButton
                isLoading={uploadingVerifica || saving}
                text={uploadingVerifica ? "Caricamento file…" : "Conferma verifica"}
                customFunc={handleVerificaCompletezza}
              />
            </span>
          </>
        }
      >
        <div className="space-y-3">
          <NotificationsList errors={formErrors} />
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="inserito_programmazione"
              checked={verificaForm.inserito_programmazione}
              onChange={(e) => setVerificaForm((f) => ({ ...f, inserito_programmazione: e.target.checked }))}
              className="h-4 w-4 text-blue-600 rounded cursor-pointer"
            />
            <label htmlFor="inserito_programmazione" className="text-sm text-gray-700 cursor-pointer">
              Inserito negli strumenti di programmazione dell&apos;ente concedente
            </label>
          </div>
          {verificaForm.inserito_programmazione && (
            <Input
              type="date"
              label="Data inserimento programmazione"
              value={verificaForm.data_inserimento_programmazione}
              onChange={(e) => setVerificaForm((f) => ({ ...f, data_inserimento_programmazione: e.target.value }))}
            />
          )}
          <TextArea
            label="Note"
            value={verificaForm.note_programmazione}
            onChange={(e) => setVerificaForm((f) => ({ ...f, note_programmazione: e.target.value }))}
            placeholder="Note sulla verifica di completezza..."
            rows={3}
          />
          {/* Allegato opzionale */}
          <div className="border-t border-gray-100 pt-3">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              Allegato (opzionale)
            </p>
            <p className="text-xs text-gray-500 mb-2">
              Puoi allegare il verbale o l&apos;atto di verifica della completezza degli elaborati.
            </p>
            <Select
              label="Tipo documento"
              value={tipoFileVerifica}
              onChange={(e) => setTipoFileVerifica(e.target.value)}
              options={[
                { value: "verbale",        label: "Verbale di verifica" },
                { value: "delibera",       label: "Delibera" },
                { value: "determinazione", label: "Determinazione" },
                { value: "altro",          label: "Altro documento" },
              ]}
            />
            <div className="mt-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">File</label>
              <input type="file" ref={fileVerificaRef} className="text-sm text-gray-600" />
            </div>
          </div>
        </div>
      </PppDialog>

      {/* Dialog — approva PFTE */}
      <PppDialog
        open={openApprova}
        title="Approva PFTE — Conclusione Fase 13"
        onClose={() => setOpenApprova(false)}
        footer={
          <>
            <SimpleButton text="Annulla" customFunc={() => setOpenApprova(false)} />
            <LoadingButton isLoading={saving} text="Approva PFTE" customFunc={handleApprova} />
          </>
        }
      >
        <div className="space-y-3">
          <NotificationsList errors={formErrors} />
          <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-800">
            L'approvazione del PFTE conclude la <strong>Fase 13</strong> e abilita l'avvio della Fase 14
            (Predisposizione documentazione di gara).
          </div>
          <p className="text-sm text-gray-600">
            Conferma l'approvazione del Progetto di Fattibilità Tecnica ed Economica integrato
            ai sensi dell'art. 38 D.lgs 36/2023.
          </p>
        </div>
      </PppDialog>
    </div>
  );
}
